import { ArtifactCard } from '@/components/artifact/ArtifactCard'
import { Button } from '@/components/ui/button'
import { DropZone } from '@/components/ui/custom/DropZone'
import { ErrorBoundary } from '@/components/ui/custom/ErrorBoundary'
import { SkeletonLoader } from '@/components/ui/custom/SkeletonLoader'
import { ViewControls } from '@/components/ui/custom/ViewControls'
import { ViewControlsSkeleton } from '@/components/ui/custom/ViewControlsSkeleton'
import { VirtualizedGrid } from '@/components/ui/custom/VirtualizedGrid'
import { VirtualizedList } from '@/components/ui/custom/VirtualizedList'
import { htmlParser } from '@/services/htmlParser'
import { useArtifactStore } from '@/store/artifactStore'
import { useUIStore } from '@/store/uiStore'
import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'

export const Route = createFileRoute('/')({
  component: () => <ArtifactsIndex />
})

function ArtifactsIndex() {
  const {
    artifacts,
    isLoading,
    error,
    createArtifact,
    deleteArtifact,
    loadArtifacts,
  } = useArtifactStore()

  const {
    viewMode,
    sortOption,
    setError,
    clearError,
    setCurrentView,
  } = useUIStore()

  const [showDropZone, setShowDropZone] = useState(false)

  // Sort artifacts based on current sort option
  const sortedArtifacts = [...artifacts].sort((a, b) => {
    const { field, direction } = sortOption
    let aValue: any
    let bValue: any

    switch (field) {
      case 'title':
        aValue = a.title.toLowerCase()
        bValue = b.title.toLowerCase()
        break
      case 'createdAt':
        aValue = new Date(a.createdAt).getTime()
        bValue = new Date(b.createdAt).getTime()
        break
      case 'updatedAt':
        aValue = new Date(a.updatedAt).getTime()
        bValue = new Date(b.updatedAt).getTime()
        break
      case 'size':
        aValue = a.metadata.size
        bValue = b.metadata.size
        break
      default:
        return 0
    }

    if (direction === 'asc') {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0
    }
  })

  useEffect(() => {
    setCurrentView('artifacts')
    loadArtifacts()
  }, [loadArtifacts, setCurrentView])

  // Update document title
  useEffect(() => {
    const count = artifacts.length
    document.title = count > 0 ? `Artifacts (${count})` : 'Artifacts'
  }, [artifacts.length])

  useEffect(() => {
    setShowDropZone(artifacts.length === 0 && !isLoading)
  }, [artifacts.length, isLoading])

  const handleArtifactDelete = async (artifactId: string) => {
    try {
      await deleteArtifact(artifactId)
    } catch (error) {
      console.error('Failed to delete artifact:', error)
      setError('Failed to delete artifact')
    }
  }

  const handleFileDrop = async (files: FileList) => {
    clearError()

    try {
      for (const file of Array.from(files)) {
        if (file.type === 'text/html' || file.name.endsWith('.html')) {
          const content = await file.text()
          const filename = file.name.replace(/\.[^/.]+$/, "")

          const title = htmlParser.extractTitle(content) || filename || 'Untitled HTML'

          await createArtifact({
            title,
            htmlContent: content,
          })
        }
      }
    } catch (error) {
      console.error('Failed to process files:', error)
      setError('Failed to process dropped files')
    }
  }

  const renderListItem = ({ index, style, data }: { index: number; style: React.CSSProperties; data: any[] }) => {
    const artifact = data[index]
    if (!artifact) return null

    return (
      <div style={style}>
        <ArtifactCard
          artifact={artifact}
          onDelete={handleArtifactDelete}
          className="w-full"
        />
      </div>
    )
  }

  const renderGridItem = ({ index, style, data }: { index: number; style: React.CSSProperties; data: any[] }) => {
    const artifact = data[index]
    if (!artifact) return null

    return (
      <div style={style}>
        <ArtifactCard
          artifact={artifact}
          onDelete={handleArtifactDelete}
          className="h-full"
        />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center space-y-4">
          <p className="text-destructive text-lg">Error: {error}</p>
          <Button onClick={() => window.location.reload()}>
            Reload Page
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      {showDropZone && (
        <div className="py-8">
          <DropZone
            onDrop={handleFileDrop}
            accept=".html"
            multiple={true}
            disabled={isLoading}
          />
        </div>
      )}

      {isLoading ? (
        <ViewControlsSkeleton />
      ) : (
        artifacts.length > 0 && <ViewControls />
      )}

      <div className="flex-1 min-h-0">
        {artifacts.length === 0 && !isLoading && !showDropZone ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center space-y-4">
              <h2 className="text-2xl font-bold text-muted-foreground">No artifacts yet</h2>
              <p className="text-muted-foreground">Create your first artifact to get started</p>
              <Button
                onClick={() => setShowDropZone(true)}
                variant="outline"
              >
                Upload HTML Files
              </Button>
            </div>
          </div>
        ) : isLoading ? (
          <div className="p-4">
            {viewMode.type === 'list' ? (
              <SkeletonLoader variant="list" count={8} />
            ) : (
              <SkeletonLoader variant="card" count={viewMode.itemsPerRow ? viewMode.itemsPerRow * 3 : 9} />
            )}
          </div>
        ) : artifacts.length > 0 ? (
          <ErrorBoundary resetKeys={[sortedArtifacts.length]}>
            {viewMode.type === 'list' ? (
              <VirtualizedList
                items={sortedArtifacts}
                renderItem={renderListItem}
                itemHeight={120}
                className="h-full"
              />
            ) : (
              <VirtualizedGrid
                items={sortedArtifacts}
                renderItem={renderGridItem}
                itemsPerRow={viewMode.itemsPerRow || 3}
                itemHeight={140}
                className="h-full"
              />
            )}
          </ErrorBoundary>
        ) : null}
      </div>
    </div>
  )
} 